import {useCallback, useEffect, useRef} from 'react';
import {DownloadCard} from '../../components/download/DownloadCard';
import LangSelection from '../../components/langSelection/LangSelection';
import {Language, supportedLang} from '../../constants/language.constants';
import useLanguage from '../../hooks/useLanguage';
import './LaunchPage.scss';
import {PageType} from '../../download/constants';
import {isAndroid, isIOS} from 'react-device-detect';
import {Platform, PlatformMap} from '../../constants/app.constants';
import useQuery from '../../hooks/useQuery';
import {InviteParameters} from '../../download/interface';

type Screen = 'magiclinkLogIn' | 'password' | 'profile' | 'story';
const screens: {[S in Screen]: true} = {
  magiclinkLogIn: true,
  password: true,
  profile: true,
  story: true,
};

type LaunchParameters = {
  totp?: string | null;
  goto?: string | null;
  profileId?: string | null;
  storyId?: string | null;
};

function getURL(
  query: URLSearchParams,
  params: Partial<LaunchParameters> | null | undefined,
  routingGoto: string | undefined,
) {
  const totp = query.get('totp');
  const goto =
    routingGoto ?? query.get('goto') ?? (totp ? 'magiclinkLogIn' : undefined); // default to magiclinkLogIn if totp is present for backward compatibility
  if (goto && !(goto in screens)) {
    return;
  }

  return buildURL({
    totp,
    goto,
    profileId: params?.profileId ?? query.get('profileId'),
    storyId: params?.storyId ?? query.get('storyId'),
  });
}

function buildURL(params: LaunchParameters) {
  const {totp, goto, profileId, storyId} = params;
  let href = 'slipapp://';
  switch (goto) {
    case 'magiclinkLogIn':
      href = `slipapp://goto/${goto}/${totp ? `?totp=${totp}` : ''}`;
      break;
    case 'profile':
      href = `slipapp://goto/${goto}/?id=${profileId}`;
      break;
    case 'story':
      href = `slipapp://goto/${goto}/?profileId=${profileId}&id=${storyId}`;
      break;
  }

  return href;
}

export function LaunchPage({
  language,
  parameters,
  type,
  goto: routingGoto,
  ready,
}: {
  language?: Language;
  parameters?: InviteParameters | null;
  type?: PageType;
  goto?: Screen;
  ready?: boolean;
}): JSX.Element {
  const [lang, setLang] = useLanguage(language);
  const ref = useRef<HTMLAnchorElement>(null);
  const query = useQuery();

  const onClick = useCallback(() => {
    const href = getURL(query, parameters, routingGoto);
    if (href) {
      window.location.href = href;
    }

    // Close window if we have lost focus (i.e. app has opened) within 200 ms
    setTimeout(() => {
      if (document.hasFocus()) {
        return;
      }
      window.close();
    }, 200);
  }, [query, parameters, routingGoto]);

  useEffect(() => {
    if (ready !== false) {
      setTimeout(() => {
        if (ref.current) {
          ref.current.click();
        }
      }, 10);
    }
  }, [ready]);

  const platforms: Platform[] | undefined = isAndroid
    ? [PlatformMap.Android]
    : isIOS
    ? [PlatformMap.iOS]
    : undefined;

  return (
    <section className="App-section-container Download-Page">
      <LangSelection
        languages={supportedLang}
        selected={lang}
        onSelect={selected => {
          supportedLang.includes(selected as Language) &&
            setLang(selected as Language);
        }}
      />
      <a ref={ref} id="launch" target="_blank" onClick={onClick}>
        <DownloadCard
          language={lang}
          type={type ?? PageType.LAUNCH}
          platforms={platforms}
          parameters={parameters}
        />
      </a>
    </section>
  );
}
